/**
 * ConnectionsAiTab
 *
 * "AI" tab on the Connections page (/connections/ai).
 * Displays all of the current user's approved AI use cases and lets them:
 *   - See which Application a use case is linked to (1:1 constraint)
 *   - Link an unlinked use case to an existing Application
 *   - Create a new AI System pre-loaded with the use case
 *
 * Data strategy (MVP): client-side join via useUseCaseApplicationMap.
 * Replace with federated AiUseCase.linkedApplication field when available.
 */
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import {
  DevHubAlert,
  DevHubAutocomplete,
  DevHubBox,
  DevHubButton,
  DevHubChip,
  DevHubCircularProgress,
  DevHubDialog,
  DevHubDialogActions,
  DevHubDialogContent,
  DevHubDialogTitle,
  DevHubGrid,
  DevHubIconButton,
  DevHubLink,
  DevHubPaper,
  DevHubTextField,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import CloseIcon from '@material-ui/icons/Close';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
  useUseCaseApplicationMap,
  UseCaseWithApplication,
} from '../../ai-system/useUseCaseApplicationMap';
import { isActionableUseCase } from '../../ai-system/useApprovedUseCases';
import { CreateAISystemModal } from '../../ai-system/CreateAISystemModal';
import { UnlinkUseCaseDialog } from '../../ai-system/UnlinkUseCaseDialog';
import { AddUseCaseToApplicationMutation } from '../../ai-system/queries';
import { MyApp } from '../../apps/types/myAppTypes';
import { AiUseCase } from '../../ai-system/types';

const STATUS_COLORS: Record<string, 'primary' | 'default' | 'secondary'> = {
  APPROVED: 'primary',
  APPROVED_WITH_CONDITIONS: 'default',
  AUTO_APPROVED: 'primary',
};

/**
 * Inline dialog for linking a use case to an existing application.
 * @param root0 - Dialog props including useCase, applications, open/close handlers, link state.
 */
const LinkToApplicationDialog = ({
  useCase,
  applications,
  open,
  onClose,
  onLink,
  error,
  isLinking,
}: {
  useCase: AiUseCase;
  applications: MyApp[];
  open: boolean;
  onClose: () => void;
  onLink: (app: MyApp) => void;
  error?: string | null;
  isLinking?: boolean;
}) => {
  const [selected, setSelected] = useState<MyApp | null>(null);

  const handleConfirm = () => {
    if (selected) onLink(selected);
  };

  const handleClose = () => {
    setSelected(null);
    onClose();
  };

  return (
    <DevHubDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{ style: { width: '480px' } }}
    >
      <DevHubDialogTitle>
        <DevHubGrid
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <DevHubTypography variant="h5" weight="light" fontColor="purpleBlue">
            Link to Application
          </DevHubTypography>
          <DevHubIconButton onClick={handleClose}>
            <CloseIcon />
          </DevHubIconButton>
        </DevHubGrid>
      </DevHubDialogTitle>
      <DevHubDialogContent>
        <DevHubBox padding="medium">
          <DevHubTypography variant="body2" style={{ marginBottom: '16px' }}>
            Select the application to link to use case{' '}
            <strong>{useCase.useCaseId}</strong>
            {useCase.title ? ` — ${useCase.title}` : ''}.
          </DevHubTypography>
          <DevHubAutocomplete
            options={applications}
            getOptionLabel={(app: MyApp) =>
              `${app.name} (ID: ${app.applicationId})`
            }
            value={selected}
            onChange={(_: unknown, value: MyApp | null) => setSelected(value)}
            renderInput={(params: object) => (
              <DevHubTextField
                {...params}
                label="Application"
                placeholder="Search your applications..."
                variant="outlined"
                size="medium"
                fullWidth
                noMargin
              />
            )}
          />
          {selected?.systemType === 'AiSystem' && (
            <DevHubAlert severity="info" style={{ marginTop: '12px' }}>
              This is an AI System. The use case will be governed under it.
            </DevHubAlert>
          )}
          {useCase.overallIntakeStatus === 'APPROVED_WITH_CONDITIONS' &&
            selected && (
              <DevHubAlert severity="warning" style={{ marginTop: '12px' }}>
                This use case was approved with conditions. Review the
                conditions in CaaS before proceeding.
              </DevHubAlert>
            )}
          {error && (
            <DevHubAlert severity="error" style={{ marginTop: '12px' }}>
              {error}
            </DevHubAlert>
          )}
        </DevHubBox>
      </DevHubDialogContent>
      <DevHubDialogActions
        style={{ padding: '16px 24px', justifyContent: 'space-between' }}
      >
        <DevHubButton
          variant="outlined"
          onClick={handleClose}
          disabled={isLinking}
        >
          Cancel
        </DevHubButton>
        <DevHubButton
          variant="contained"
          color="primary"
          disabled={!selected || isLinking}
          onClick={handleConfirm}
        >
          {isLinking ? <DevHubCircularProgress size={20} /> : 'Link'}
        </DevHubButton>
      </DevHubDialogActions>
    </DevHubDialog>
  );
};

/** AI tab on the Connections page — shows all approved use cases with link/unlink actions. */
export const ConnectionsAiTab = () => {
  const { rows, applications, loading, refetching, error, reload } =
    useUseCaseApplicationMap();

  const [createModalUseCaseId, setCreateModalUseCaseId] = useState<
    string | undefined
  >(undefined);
  const [linkDialogRow, setLinkDialogRow] =
    useState<UseCaseWithApplication | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [unlinkConfirmRow, setUnlinkConfirmRow] =
    useState<UseCaseWithApplication | null>(null);

  const [addUseCaseToApplication] = useMutation(
    AddUseCaseToApplicationMutation,
  );

  const handleCreateAiSystem = (useCaseId: string) => {
    setCreateModalUseCaseId(useCaseId);
  };

  const handleLink = async (app: MyApp) => {
    if (!linkDialogRow) return;
    setLinkError(null);
    setIsLinking(true);
    try {
      const { data } = await addUseCaseToApplication({
        variables: {
          input: {
            applicationId: app.applicationId,
            useCaseId: linkDialogRow.useCase.useCaseId,
          },
        },
      });
      const result = data?.resources?.application?.addUseCaseToApplication;
      if (result?.success) {
        setLinkDialogRow(null);
        reload();
      } else {
        setLinkError(result?.message ?? 'Link failed. Please try again.');
      }
    } catch (e: any) {
      setLinkError(e?.message ?? 'Link failed. Please try again.');
    } finally {
      setIsLinking(false);
    }
  };

  const renderStatus = (row: UseCaseWithApplication) => {
    const status = row.useCase.overallIntakeStatus ?? row.useCase.status;
    if (!status) {
      return (
        <DevHubTooltip title="Status information is not yet available for this use case.">
          <span>
            <DevHubTypography variant="body2" opacity="hazy">
              —
            </DevHubTypography>
          </span>
        </DevHubTooltip>
      );
    }
    const chip = (
      <DevHubChip
        size="small"
        label={status}
        color={STATUS_COLORS[status] ?? 'default'}
      />
    );
    if (status === 'UNEXPECTED_VALUE') {
      return (
        <DevHubTooltip title="This use case has a status value not yet recognised by this system. Check CaaS directly for the current status.">
          <span>{chip}</span>
        </DevHubTooltip>
      );
    }
    return chip;
  };

  const renderLinkedApplication = (row: UseCaseWithApplication) => {
    const app = row.linkedApplication;
    if (!app) {
      return (
        <DevHubTypography variant="body2" opacity="hazy">
          Not linked
        </DevHubTypography>
      );
    }
    return (
      <DevHubBox
        display="flex"
        alignItems="center"
        style={{ gap: '8px', flexWrap: 'wrap' }}
      >
        <DevHubLink
          to={`/catalog/default/system/${app.applicationId}`}
          component={RouterLink}
        >
          {app.name}
        </DevHubLink>
        {app.systemType === 'AiSystem' && (
          <DevHubChip size="small" label="AI System" color="primary" />
        )}
      </DevHubBox>
    );
  };

  const renderActions = (row: UseCaseWithApplication) => {
    const actionable = isActionableUseCase(row.useCase);
    if (row.linkedApplication) {
      return (
        <DevHubButton
          size="small"
          variant="outlined"
          onClick={() => setUnlinkConfirmRow(row)}
        >
          Unlink
        </DevHubButton>
      );
    }
    const disabledTooltip =
      'This use case must be approved before it can be linked to an application.';
    const linkButton = (
      <DevHubButton
        size="small"
        variant="outlined"
        disabled={!actionable}
        onClick={() => setLinkDialogRow(row)}
      >
        Link to existing
      </DevHubButton>
    );
    const createButton = (
      <DevHubButton
        size="small"
        variant="outlined"
        disabled={!actionable}
        onClick={() => handleCreateAiSystem(row.useCase.useCaseId)}
      >
        Create AI System
      </DevHubButton>
    );
    return (
      <DevHubBox display="flex" style={{ gap: '8px', flexWrap: 'wrap' }}>
        {actionable ? (
          linkButton
        ) : (
          <DevHubTooltip title={disabledTooltip}>
            <span>{linkButton}</span>
          </DevHubTooltip>
        )}
        {actionable ? (
          createButton
        ) : (
          <DevHubTooltip title={disabledTooltip}>
            <span>{createButton}</span>
          </DevHubTooltip>
        )}
      </DevHubBox>
    );
  };

  if (loading) return <Progress />;

  if (error) {
    return (
      <DevHubAlert severity="error" style={{ marginTop: '16px' }}>
        Could not load your use cases. This may be a temporary issue — try
        refreshing the page.
        {error.message && (
          <DevHubTypography
            variant="body2"
            style={{ marginTop: '4px', opacity: 0.8 }}
          >
            {error.message}
          </DevHubTypography>
        )}
      </DevHubAlert>
    );
  }

  return (
    <>
      <DevHubGrid container>
        <DevHubGrid item xs={12}>
          <DevHubBox style={{ marginBottom: '16px' }}>
            <DevHubTypography variant="body2" opacity="hazy">
              Link your approved use cases to an Application or AI System you
              already own, or create a new AI System directly from a use case.
            </DevHubTypography>
            <DevHubTypography
              variant="body2"
              opacity="hazy"
              style={{ marginTop: '4px' }}
            >
              Need to link a use case to a new Business Application instead?{' '}
              <DevHubLink to="/onboarding" component={RouterLink}>
                Create one here →
              </DevHubLink>{' '}
              Then return here to link your use case.
            </DevHubTypography>
          </DevHubBox>
          {refetching && rows.length === 0 && (
            <DevHubBox
              display="flex"
              alignItems="center"
              style={{ gap: '8px', marginBottom: '8px' }}
            >
              <DevHubCircularProgress size={16} />
              <DevHubTypography variant="body2" opacity="hazy">
                Updating...
              </DevHubTypography>
            </DevHubBox>
          )}
          {rows.length === 0 && !refetching ? (
            <DevHubPaper>
              <DevHubBox padding="large" display="flex" justifyContent="center">
                <DevHubBox style={{ textAlign: 'center', maxWidth: '480px' }}>
                  <DevHubTypography
                    variant="h6"
                    style={{ marginBottom: '8px' }}
                  >
                    No use cases yet
                  </DevHubTypography>
                  <DevHubTypography variant="body2" opacity="hazy">
                    Use cases you submit through the AI governance process will
                    appear here. You can link approved ones to an application.
                  </DevHubTypography>
                </DevHubBox>
              </DevHubBox>
            </DevHubPaper>
          ) : (
            <DevHubPaper>
              <DataTable
                value={rows}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                responsiveLayout="scroll"
                dataKey="useCase.useCaseId"
                rowHover
                loading={refetching}
                sortField="useCase.useCaseId"
                sortOrder={1}
              >
                <Column
                  field="useCase.useCaseId"
                  header="Use Case ID"
                  style={{ width: '140px' }}
                  body={(row: UseCaseWithApplication) =>
                    row.useCase.useCaseSysId ? (
                      <DevHubLink
                        href={`https://comcastsec.service-now.com/now/nav/ui/classic/params/target/x_mcse_aim_ai_intake_request.do%3Fsys_id%3D${row.useCase.useCaseSysId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.useCase.useCaseId}
                      </DevHubLink>
                    ) : (
                      <DevHubTypography variant="body2">
                        {row.useCase.useCaseId}
                      </DevHubTypography>
                    )
                  }
                />
                <Column
                  field="useCase.title"
                  header="Title"
                  style={{ width: 'auto' }}
                  body={(row: UseCaseWithApplication) => (
                    <DevHubTypography variant="body2">
                      {row.useCase.title}
                    </DevHubTypography>
                  )}
                />
                <Column
                  header="Status"
                  style={{ width: '180px' }}
                  body={renderStatus}
                />
                <Column
                  header="Linked Application"
                  style={{ width: '240px' }}
                  body={renderLinkedApplication}
                />
                <Column
                  header="Actions"
                  style={{ width: '320px' }}
                  body={renderActions}
                />
              </DataTable>
            </DevHubPaper>
          )}
        </DevHubGrid>
      </DevHubGrid>

      <CreateAISystemModal
        open={!!createModalUseCaseId}
        onClose={() => setCreateModalUseCaseId(undefined)}
        lockedUseCaseId={createModalUseCaseId}
        onSuccess={() => {
          setCreateModalUseCaseId(undefined);
          reload();
        }}
      />

      {linkDialogRow && (
        <LinkToApplicationDialog
          useCase={linkDialogRow.useCase}
          applications={applications}
          open
          onClose={() => {
            setLinkDialogRow(null);
            setLinkError(null);
          }}
          onLink={handleLink}
          error={linkError}
          isLinking={isLinking}
        />
      )}

      {unlinkConfirmRow?.linkedApplication && (
        <UnlinkUseCaseDialog
          useCaseId={unlinkConfirmRow.useCase.useCaseId}
          applicationId={unlinkConfirmRow.linkedApplication.applicationId}
          applicationName={unlinkConfirmRow.linkedApplication.name}
          open
          onClose={() => setUnlinkConfirmRow(null)}
          onSuccess={() => {
            setUnlinkConfirmRow(null);
            reload();
          }}
        />
      )}
    </>
  );
};
